import React, { ReactNode } from "react";
import AuthService, { LoginParams, MeResponse } from "../services/AuthService";
import UserService from "../services/UserService";
import { unwrapData } from "../services/apiUtils";
import { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../services/Axios";

type AuthUser = {
  userId: number;
  name: string;
  email: string;
  roleName?: string;
  country?: string;
  avatarUrl?: string;
};

type ContextObj = {
  token: string;
  user: AuthUser | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  loginError: string | null;
  login: (params: LoginParams) => Promise<boolean>;
  logout: () => void;
  updateAvatar: (avatarUrl: string) => void;
};

const AuthContext = React.createContext<ContextObj>({
  token: "",
  user: null,
  isLoggedIn: false,
  isLoading: false,
  loginError: null,
  login: async () => false,
  logout: () => {},
  updateAvatar: () => {},
});

interface StateGlobalProviderProps {
  children: ReactNode;
}

export const AuthContextProvider: React.FC<StateGlobalProviderProps> = ({
  children,
}: StateGlobalProviderProps) => {
  const initialToken = localStorage.getItem(AUTH_TOKEN_KEY) || "";

  let initialUser: AuthUser | null = null;
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      initialUser = JSON.parse(storedUser);
    } catch {
      initialUser = null;
    }
  }

  const getStoredAvatar = (userId?: number): string | undefined => {
    if (!userId) return undefined;
    return localStorage.getItem(`avatarUrl:${userId}`) || undefined;
  };

  if (initialUser) {
    initialUser.avatarUrl = getStoredAvatar(initialUser.userId);
  }

  const [token, setToken] = React.useState(initialToken);
  const [user, setUser] = React.useState<AuthUser | null>(initialUser);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState<string | null>(null);

  const userIsLoggedIn = !!token;

  const logoutHandler = () => {
    setToken("");
    setUser(null);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem("user");
  };

  const loginHandler = async (params: LoginParams): Promise<boolean> => {
    setIsLoading(true);
    setLoginError(null);
    try {
      const loginRes = await AuthService.login(params);
      localStorage.setItem(AUTH_TOKEN_KEY, loginRes.accessToken);

      if (loginRes.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, loginRes.refreshToken);
      }
      setToken(loginRes.accessToken);

      let me: MeResponse | null = null;
      try {
        me = await AuthService.getMe();
      } catch {

        me = null;
      }

      let country: string | undefined = loginRes.country;
      if (!country) {
        try {
          const fullUser = unwrapData(await UserService.getUserById(loginRes.userId));
          country = fullUser?.country || undefined;
        } catch {
          country = undefined;
        }
      }

      const authUser: AuthUser = {
        userId: loginRes.userId,
        name: me?.name || loginRes.name,
        email: me?.email || loginRes.email,
        roleName: me?.roles?.roleName || loginRes.role,
        country,
        avatarUrl: getStoredAvatar(loginRes.userId),
      };
      setUser(authUser);
      localStorage.setItem("user", JSON.stringify(authUser));
      setIsLoading(false);
      return true;
    } catch (error: any) {
      setIsLoading(false);
      localStorage.removeItem(AUTH_TOKEN_KEY);
      setToken("");
      setLoginError(
        error?.response?.data?.message ||
          error?.friendlyMessage ||
          "Invalid email/phone or password."
      );
      return false;
    }
  };

  const updateAvatarHandler = (avatarUrl: string) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, avatarUrl };
      localStorage.setItem("user", JSON.stringify(next));
      localStorage.setItem(`avatarUrl:${prev.userId}`, avatarUrl);
      return next;
    });
  };

  const contextValue: ContextObj = {
    token,
    user,
    isLoggedIn: userIsLoggedIn,
    isLoading,
    loginError,
    login: loginHandler,
    logout: logoutHandler,
    updateAvatar: updateAvatarHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
